<div class="entity-config-page container-fluid px-0 py-0 py-lg-1">
  <div class="entity-config-shell d-flex flex-column gap-3 mx-auto">
    <section class="entity-config-top rounded-4 overflow-hidden shadow-sm">
      <div class="entity-config-top-hero px-2 px-md-3 py-3">
        <div class="row g-2 align-items-start flex-nowrap">
          <div class="col">
            <h1 class="entity-page-title text-white fw-bold lh-sm mb-1">{{ verification_form_display_name }}</h1>
            <p class="entity-page-subtitle text-white mb-0">
              Map required fields and onboarding questions in one place.
            </p>
          </div>

          <div class="col-auto d-flex justify-content-end align-items-start gap-2">
            <button
              type="button"
              class="btn btn-light btn-sm rounded-3 text-xs px-3 shadow-sm d-inline-flex align-items-center gap-2"
              @click="go_back"
            >
              <i class="bi bi-arrow-left"></i>
              <span>Back</span>
            </button>
            <button
              type="button"
              class="btn btn-primary-dark text-xs d-inline-flex align-items-center rounded-3 px-3 py-2 shadow-sm me-2 align-self-end"
              :disabled="loading"
              @click="go_to_question_builder"
            >
              <span class="fw-semibold">Create questions</span>
            </button>
          </div>
        </div>
      </div>
    </section>

    <section v-if="loading" class="card border-0 shadow-sm rounded-4">
      <div class="card-body p-4 text-secondary text-xs d-flex align-items-center gap-2">
        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        <span>Loading verification configuration...</span>
      </div>
    </section>

    <section v-else class="d-flex flex-column gap-3">
      <div class="d-flex flex-wrap gap-2 border-bottom pb-3 mb-1">
        <button
          type="button"
          class="btn btn-sm rounded-pill px-3 workflow-tab-btn"
          :class="active_tab === 'step1' ? 'workflow-tab-active' : 'workflow-tab-inactive'"
          @click="active_tab = 'step1'"
        >
          Question Mapping
        </button>
        <button
          type="button"
          class="btn btn-sm rounded-pill px-3 workflow-tab-btn"
          :class="active_tab === 'step23' ? 'workflow-tab-active' : 'workflow-tab-inactive'"
          @click="active_tab = 'step23'"
        >
          Service Setup
        </button>
      </div>

      <div v-if="active_tab === 'step1'" class="d-flex flex-column gap-3">
            <div v-if="!verification_survey_form_id" class="alert alert-light border text-secondary text-xs py-2 px-3 mb-0">
              <i class="bi bi-exclamation-triangle me-2"></i>
              Verification form is missing a <b>survey_form_id</b>, so question mapping is unavailable.
            </div>

            <template v-else>
              <keep-alive>
                <FormQuestionMapping
                  :key="`${selected_onboarding_form_id || 'none'}-${verification_survey_form_id || 'verification'}`"
                  embedded
                  :onboarding_form_id="selected_onboarding_form_id"
                  :onboarding_form_name="selected_onboarding_form_name"
                  :verification_survey_form_id="verification_survey_form_id"
                  :verification_form_name="verification_form_display_name"
                  :form_link_id="get_form_link_id(selected_onboarding_link)"
                />
              </keep-alive>
            </template>
      </div>

      <div v-else-if="active_tab === 'step23'" class="d-flex flex-column gap-3">
        <div class="row g-3">
        <div class="col-12 col-lg-4">
          <article class="card border border-purple border-opacity-50 shadow-sm rounded-4 h-100">
            <div class="card-body p-3 p-md-4">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <div class="fw-bold text-xs">Step 2 — Choose a service</div>
                <button
                  type="button"
                  class="btn btn-light btn-sm rounded-3 text-xxs px-3"
                  @click="go_to_next_incomplete_service"
                  :disabled="!(services || []).length"
                >
                  Next incomplete
                </button>
              </div>
              <div class="text-secondary text-xxs mb-3">
                Each service has its own required fields. Select a service to map its fields.
              </div>

              <div v-if="!(services || []).length" class="text-secondary text-xs">
                No verification services found.
              </div>

              <div v-else class="d-flex flex-column gap-2">
                <div class="verification-service-scroll d-flex flex-column gap-2 pe-1">
                  <button
                    v-for="service in (services || [])"
                    :key="service.id"
                    type="button"
                    class="btn w-100 text-start rounded-4 border p-3 d-flex align-items-start justify-content-between gap-3 position-relative"
                    :class="String(active_service_id) === String(service.id) ? 'btn-light border-primary' : 'btn-white bg-white'"
                    @click="set_active_service(service.id)"
                  >
                    <div class="min-w-0">
                      <div class="fw-semibold text-x text-truncate">{{ service.name || service.title }}</div>
                      <div class="text-secondary text-xxs text-truncate">{{ service.description || 'Service configuration.' }}</div>
                      <div class="mt-2">
                        <span class="badge bg-primary-subtle text-primary border rounded-pill text-xxs fw-semibold px-2 py-1">
                          {{ get_service_progress(service).mapped }}/{{ get_service_progress(service).total }} required fields mapped
                        </span>
                      </div>
                    </div>

                    <div class="flex-shrink-0 pt-1">
                      <i
                        class="bi"
                        :class="(get_service_progress(service).total > 0 && get_service_progress(service).missing.length === 0) ? 'bi-check-circle text-success' : 'bi-exclamation-triangle text-warning'"
                        v-if="get_service_progress(service).total > 0"
                      ></i>
                      <i class="bi bi-dash-circle text-secondary" v-else></i>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </article>
        </div>

        <div class="col-12 col-lg-8">
          <article class="card border border-purple border-opacity-50 shadow-sm rounded-4 h-100">
            <div class="card-body p-3 p-md-4">
              <div class="fw-bold text-xs mb-2">Step 3 — Map required fields</div>
              <div class="text-secondary text-xxs mb-3">
                For the selected service, map each required field to a verification question.
              </div>

              <div v-if="!active_service" class="text-secondary text-xs">
                Select a service on the left to start mapping.
              </div>

              <div v-else>
                <div class="d-flex align-items-start justify-content-between gap-3 mb-3">
                  <div class="min-w-0">
                    <div class="fw-bold text-x mb-1">{{ active_service.name || active_service.title }}</div>
                    <div class="text-secondary text-xs">Required fields for this service only.</div>
                  </div>
                  <div class="text-secondary text-xxs d-flex align-items-center gap-2" v-if="active_service_state?.loading">
                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    <span>Loading…</span>
                  </div>
                </div>

                <div
                  v-if="!(active_service_state?.questions || []).length"
                  class="text-secondary text-xxs mb-3"
                >
                  Create verification questions first to enable required-field mapping.
                </div>

                <div
                  v-else-if="active_service_state?.mapping_options_message && !(active_service_state?.required_fields || []).length"
                  class="text-secondary text-xxs mb-3"
                >
                  {{ active_service_state?.mapping_options_message }}
                </div>

                <div v-if="(active_service_state?.required_fields || []).length" class="d-flex flex-column gap-2">
                  <div
                    v-for="field in (active_service_state?.required_fields || [])"
                    :key="`${active_service.id}-${field.key || field.name || field}`"
                    class="row g-2 align-items-center"
                  >
                    <div class="col-12 col-md-4">
                      <div class="text-xs fw-semibold mb-1">{{ format_required_field_label(field.key || field.name || field) }}</div>
                    </div>
                    <div class="col-12 col-md-8">
                      <select
                        class="form-select form-select-sm rounded-3 text-xs"
                        :value="(active_service_state?.field_mappings || {})[String(field.key || field.name || field)] || ''"
                        @change="on_field_mapping_change(active_service.id, field, $event)"
                        :disabled="!(active_service_state?.questions || []).length"
                      >
                        <option value="">
                          {{ (active_service_state?.questions || []).length ? 'Select a verification question…' : 'No verification questions available yet' }}
                        </option>
                        <option
                          v-for="question in (active_service_state?.questions || [])"
                          :key="question.id"
                          :value="question.id"
                        >
                          {{ question.label }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>

                <div v-else class="text-secondary text-xs">
                  No required fields are defined for this service.
                </div>

                <div
                  v-if="(active_service_state?.required_fields || []).length && (active_service_state?.questions || []).length"
                  class="d-flex flex-column flex-md-row justify-content-end gap-2 mt-4 pt-3 border-top"
                >
                  <button
                    type="button"
                    class="btn btn-light btn-sm rounded-3 text-xs px-3"
                    @click="discard_active_service_changes"
                    :disabled="!active_service_has_changes"
                  >
                    Discard changes
                  </button>
                  <button
                    type="button"
                    class="btn entity-assign-btn btn-sm rounded-small fw-semibold px-2 py-1"
                    @click="save_active_service"
                    :disabled="!active_service_has_changes"
                  >
                    Save mapping
                  </button>
                </div>
              </div>
            </div>
          </article>
        </div>
        </div>
      </div>
    </section>
  </div>
</div>
